import { useEffect, useRef, useState } from 'react'
import { RequestError, api } from '../../lib/api'
import { faNum } from '../platform/labels'
import { Resampler, Segmenter, TARGET_RATE, encodeWav, joinPieces } from './voice'

/** Long enough for a real answer, short enough to stay under the server's limit. */
const MAX_SECONDS = 150
/** Pieces in flight at once. Two keeps the text close behind the speaker without queueing a burst. */
const CONCURRENCY = 2

type State = 'idle' | 'recording' | 'transcribing'

interface Session {
  ctx: AudioContext
  stream: MediaStream
  /* Held on purpose. A MediaStreamAudioSourceNode nothing in JS refers to can
     be garbage-collected mid-recording, after which the worklet hears
     silence — recordings of the right length with nothing in them, which is
     what production saw. */
  source: MediaStreamAudioSourceNode
  node: AudioWorkletNode
  /** Loudest sample heard, to tell a silent microphone from a quiet speaker. */
  peak: number
  resampler: Resampler
  segmenter: Segmenter
  /** Every 16 kHz sample, for the one full transcription at the end. */
  full: Float32Array[]
  samples: number
  /** Text per piece, in the order spoken; undefined while a piece is still out. */
  pieces: (string | undefined)[]
  queue: { index: number; wav: Blob }[]
  inFlight: number
  /** Resolves when every piece sent so far has come back. */
  drained: () => Promise<void>
}

const supported = (): boolean =>
  typeof window !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof AudioContext !== 'undefined' &&
  'audioWorklet' in AudioContext.prototype

/**
 * Speak instead of typing — the way a meeting app captions, through our own
 * server.
 *
 * The microphone is read raw through an AudioWorklet, resampled to 16 kHz and
 * cut at pauses. Each piece goes to the server while the director is still
 * talking, and its text appears as it comes back, so they can see they are
 * being heard. When they stop, the whole recording is transcribed once more
 * in one piece — a word split across a cut, or a sentence that only makes
 * sense whole, comes out right — and that version goes in the composer.
 *
 * Nothing goes to a browser vendor's speech service, which from Iran may not
 * be reachable at all. The text is never sent on the director's behalf: they
 * read what was heard and send it themselves, because a mis-heard sentence
 * that reaches the interpreter becomes a mis-scored statement.
 */
export function VoiceInput({ onText, disabled }: { onText: (textFa: string) => void; disabled: boolean }) {
  const [state, setState] = useState<State>('idle')
  const [seconds, setSeconds] = useState(0)
  const [live, setLive] = useState('')
  const [error, setError] = useState<string | null>(null)
  const session = useRef<Session | null>(null)
  /* The session whose pieces the preview shows — outlives `session` through
     the final pass, so text that lands after «stop» still appears. */
  const shown = useRef<Session | null>(null)

  const teardown = (s: Session) => {
    s.node.port.onmessage = null
    s.source.disconnect()
    s.node.disconnect()
    s.stream.getTracks().forEach((t) => t.stop())
    void s.ctx.close()
  }

  useEffect(
    () => () => {
      if (session.current) teardown(session.current)
      session.current = null
    },
    [],
  )

  const pump = (s: Session) => {
    while (s.inFlight < CONCURRENCY && s.queue.length > 0) {
      const job = s.queue.shift()
      if (!job) break
      s.inFlight += 1
      api
        .transcribe(job.wav, { part: 'segment' })
        .then((r) => {
          s.pieces[job.index] = r.textFa
        })
        .catch(() => {
          // A lost piece only thins the live preview; the full pass at the end covers it.
          s.pieces[job.index] = ''
        })
        .finally(() => {
          s.inFlight -= 1
          if (shown.current === s) setLive(joinPieces(s.pieces))
          pump(s)
        })
    }
  }

  const send = (s: Session, piece: Float32Array) => {
    const index = s.pieces.length
    s.pieces.push(undefined)
    s.queue.push({ index, wav: encodeWav(piece) })
    pump(s)
  }

  const start = async () => {
    setError(null)
    setLive('')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch {
      setError('اجازه دسترسی به میکروفون داده نشد.')
      return
    }

    let ctx: AudioContext
    let node: AudioWorkletNode
    let source: MediaStreamAudioSourceNode
    try {
      ctx = new AudioContext()
      // A static file, not a Blob URL: the CSP allows scripts from 'self' only.
      await ctx.audioWorklet.addModule('/voice-worklet.js')
      node = new AudioWorkletNode(ctx, 'voice-tap')
      source = ctx.createMediaStreamSource(stream)
      source.connect(node)
      /* Connected through to the output so every browser keeps pulling the
         graph. The tap writes nothing to its output, so nothing is heard. */
      node.connect(ctx.destination)
      // Created after an await, so it may start suspended; a suspended context records nothing.
      if (ctx.state !== 'running') await ctx.resume()
    } catch {
      stream.getTracks().forEach((t) => t.stop())
      setError('این مرورگر ضبط صدا را پشتیبانی نمی‌کند.')
      return
    }

    const s: Session = {
      ctx,
      stream,
      source,
      node,
      peak: 0,
      resampler: new Resampler(ctx.sampleRate),
      segmenter: new Segmenter(),
      full: [],
      samples: 0,
      pieces: [],
      queue: [],
      inFlight: 0,
      drained: async () => {
        while (s.inFlight > 0 || s.queue.length > 0) await new Promise((r) => setTimeout(r, 100))
      },
    }
    node.port.onmessage = (event: MessageEvent<Float32Array>) => {
      for (const v of event.data) if (Math.abs(v) > s.peak) s.peak = Math.abs(v)
      const block = s.resampler.push(event.data)
      s.full.push(block)
      s.samples += block.length
      for (const piece of s.segmenter.push(block)) send(s, piece)
      setSeconds(Math.floor(s.samples / TARGET_RATE))
    }
    session.current = s
    shown.current = s
    setSeconds(0)
    setState('recording')
  }

  const stop = async () => {
    const s = session.current
    if (!s) return
    session.current = null
    teardown(s)
    setState('transcribing')

    /* The microphone delivered nothing at all — a muted input, the wrong
       device, or the browser cut the stream. Say so, rather than charge a
       transcription for silence and then report «nothing was heard». */
    if (s.peak < 0.002) {
      shown.current = null
      setState('idle')
      setLive('')
      setError(
        s.samples === 0
          ? 'میکروفون صدایی به برنامه نرساند. یک بار صفحه را تازه کنید و دوباره امتحان کنید.'
          : 'میکروفون فقط سکوت ضبط کرد. ببینید میکروفون درست انتخاب شده و بی‌صدا نیست.',
      )
      return
    }

    const tail = s.segmenter.flush()
    if (tail) send(s, tail)

    const all = new Float32Array(s.samples)
    let at = 0
    for (const block of s.full) {
      all.set(block, at)
      at += block.length
    }

    try {
      const { textFa } = await api.transcribe(encodeWav(all), {
        part: 'final',
        segments: s.pieces.length,
        peak: Math.round(s.peak * 1000) / 1000,
      })
      if (textFa.trim()) {
        onText(textFa.trim())
      } else {
        await s.drained()
        const pieced = joinPieces(s.pieces)
        if (pieced) onText(pieced)
        else setError('صدایی شنیده نشد.')
      }
    } catch (err) {
      /* The full pass failed; the pieces may still have it. */
      await s.drained()
      const pieced = joinPieces(s.pieces)
      if (pieced) {
        onText(pieced)
        setError('نوشتن کامل ممکن نشد؛ متن تکه‌تکه را گذاشتم. قبل از فرستادن نگاهش کنید.')
      } else {
        setError(err instanceof RequestError ? err.messageFa : 'نتوانستم صدا را بنویسم. دوباره امتحان کنید یا تایپ کنید.')
      }
    } finally {
      shown.current = null
      setState('idle')
      setLive('')
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: stop only reads refs
  useEffect(() => {
    if (state === 'recording' && seconds >= MAX_SECONDS) void stop()
  }, [state, seconds])

  if (!supported()) return null

  return (
    <div className="voice">
      <button
        type="button"
        className={`btn btn--ghost voice__btn ${state === 'recording' ? 'is-recording' : ''}`}
        aria-pressed={state === 'recording'}
        aria-label={state === 'recording' ? 'پایان ضبط' : 'گفتن به‌جای نوشتن'}
        title={state === 'recording' ? 'پایان ضبط' : 'گفتن به‌جای نوشتن'}
        disabled={disabled || state === 'transcribing'}
        onClick={() => (state === 'recording' ? void stop() : void start())}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          {state === 'recording' ? (
            <rect x="6" y="6" width="12" height="12" />
          ) : (
            <>
              <path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v3" />
            </>
          )}
        </svg>
      </button>
      {state === 'recording' && (
        <div className="voice__status" aria-live="polite">
          <span className="voice__dot" aria-hidden="true" /> در حال ضبط ·{' '}
          <span className="num">
            {faNum(Math.floor(seconds / 60))}:{faNum(String(seconds % 60).padStart(2, '0'))}
          </span>
          {live && <div className="voice__live">{live}</div>}
        </div>
      )}
      {state === 'transcribing' && (
        <div className="voice__status" aria-live="polite">
          در حال نوشتن آنچه گفتید…
          {live && <div className="voice__live">{live}</div>}
        </div>
      )}
      {error && <div className="voice__error">{error}</div>}
    </div>
  )
}
